import HamburgerSvg from '@assets/svg/Hamburger';
import { Button, NavigationItem, cn } from 'shared-components';
import { type FunctionComponent, useMemo } from 'react';

import { type NavigationProps } from './Navigation.types';
import { IDS } from 'e2e-page-objects/selectors';

const hasAnyRole = (itemRoles: string[], currentRoles: string[]): boolean =>
  itemRoles.some((role) => currentRoles.includes(role));

const Navigation: FunctionComponent<NavigationProps> = ({
  items,
  currentRoles,
  isAuthenticated,
  isMenuOpen,
  onToggleMenu,
  onLogout,
  logoutLabel,
  menuLabel,
  dataTestid,
  className,
}) => {
  const visibleItems = useMemo(
    () => items.filter((item) => hasAnyRole(item.roles, currentRoles)),
    [items, currentRoles],
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className={cn('flex items-center', className)} data-testid={dataTestid || 'admin-navigation'}>
      <Button
        name="admin-navigation-toggle"
        variant="ghost"
        size="icon"
        className="size-8! lg:hidden"
        aria-label={menuLabel}
        aria-expanded={isMenuOpen}
        onClick={onToggleMenu}
        startIcon={<HamburgerSvg className="size-6 shrink-0" data-testid="hamburger" />}
      />

      {isMenuOpen && (
        <nav
          data-testid={IDS.navigationDrawer}
          className={cn('fixed top-20.25 right-0 h-screen w-full overflow-y-scroll bg-white pb-25 sm:w-1/2')}
        >
          <ul className="m-0 list-none p-0">
            {visibleItems.map((item) => (
              <NavigationItem
                key={item.linkName}
                variant="headerMenu"
                linkName={item.linkName}
                name={item.label}
                icon={item.icon}
                routeParams={item.routeParams}
                onNavigationElementClicked={onToggleMenu}
              />
            ))}
            <li className="block border-0 border-b border-solid border-secondary-300 p-4">
              <Button name="admin-navigation-logout" variant="ghost" onClick={onLogout}>
                {logoutLabel}
              </Button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
};

Navigation.displayName = 'Navigation';
export { Navigation };
